import React, { useReducer, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Send, CheckCircle, AlertCircle} from 'lucide-react';
import { SectionCompanyDetails } from './Sections/SectionCompanyDetails.jsx';
import { SectionCompanyOverview } from './Sections/SectionCompanyOverview.jsx';
import { SectionExperience } from './Sections/SectionExperience.jsx';
import { SectionBenefits } from './Sections/SectionBenefits.jsx';
import { SectionReferees } from './Sections/SectionReferees.jsx';
import { SectionDeclaration } from './Sections/SectionDeclaration.jsx';
import { S } from '../styles.js';
import { initialState, reducer } from './state.js';
import { validateSection} from './util.js';
import { Stepper } from './Stepper.jsx';
import { Button } from '../../SharedInputs.jsx';
import { createNewAgentApplication } from '../../../services/AgentService.js'
import { fileStorageService } from '../../../services/FileStorageService.js'
import { v4 as uuidv4 } from "uuid";

export default function AgentApplicationForm() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [touchedSections, setTouchedSections] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sections = [
    'companyDetails',
    'companyOverview',
    'experience',
    'benefits',
    'referees',
    'declaration'
  ];

  const currentSection = sections[currentStep];
  const validation = validateSection(currentSection, state);
  const showErrors = touchedSections.includes(currentSection);

  useEffect(() => {
    const newCompleted = [];
    sections.forEach((key, index) => {
      const val = validateSection(key, state);
      if (val.valid && index < currentStep) {
        newCompleted.push(index);
      }
    });
    setCompletedSteps(newCompleted);
  }, [state, currentStep]);

  const nextStep = () => {
    setTouchedSections(prev => [...new Set([...prev, currentSection])]);
    if (validation.valid && currentStep < sections.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (index) => {
    if (index >= 0 && index < sections.length) {
      setCurrentStep(index);
    }
  };

  const handleSubmit = async () => {
    setTouchedSections([...sections]);
    
    let allValid = true;
    sections.forEach(key => {
      const val = validateSection(key, state);
      if (!val.valid) allValid = false;
    });

    if (!allValid) {
      setSubmitError('Please complete all required fields before submitting');
      return;
    }

    setSubmitError('');
    setSubmitting(true);
    try {
      const agentId = uuidv4();
      const stateWithUrls = { ...state };

      // Passport file
      if (state.experience?.trainingCertificate) {
        const fileData = await fileStorageService.uploadFile(
          state.experience.trainingCertificate,
          `agent/${agentId}/experience/${state.experience.trainingCertificate.name}`
        );
        stateWithUrls.experience.trainingCertificate = { url: fileData.url, path: fileData.path, name: state.experience.trainingCertificate.name };
      }

      // Additional documents (multiple files)
      if (state.declaration?.additionalDocuments?.length) {
        const filesData = await fileStorageService.uploadFiles(
          state.declaration.additionalDocuments,
          `agent/${agentId}/additionalDocuments`
        );
        stateWithUrls.declaration.additionalDocuments = filesData.map((f, i) => ({
          url: f.url,
          path: f.path,
          name: state.declaration.additionalDocuments[i].name
        }));
      }
      stateWithUrls.status= "new";
      // Submit the updated state to Firestore
      await createNewAgentApplication(stateWithUrls, "");

      setSubmitting(false);
      setSubmitSuccess(agentId);
    } catch (error) {
      setSubmitting(false);
      setSubmitError("Submission failed. Please try again or contact admissions support.");
    }
  };

  const renderSection = () => {
    const errors = showErrors ? validation.errors : [];

    switch (currentSection) {
      case 'companyDetails':
        return <SectionCompanyDetails state={state} dispatch={dispatch} errors={errors} />;
      case 'companyOverview':
        return <SectionCompanyOverview state={state} dispatch={dispatch} errors={errors} />;
      case 'experience':
        return <SectionExperience state={state} dispatch={dispatch} errors={errors} />;
      case 'benefits':
        return <SectionBenefits state={state} dispatch={dispatch} errors={errors} />;
      case 'referees':
        return <SectionReferees state={state} dispatch={dispatch} errors={errors} />;
      case 'declaration':
        return <SectionDeclaration state={state} dispatch={dispatch} errors={errors} />;
      default:
        return null;
    }
  };

  if (submitSuccess) {
    return (
      <div style={S.page}>
        <motion.div
          style={S.card}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div style={{ textAlign: 'center', padding: 40 }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <CheckCircle size={64} color="#10b981" style={{ marginBottom: 20 }} />
            </motion.div>

            <h1 style={{ ...S.h1, marginBottom: 16 }}>Application Submitted Successfully!</h1>
            <p style={{ fontSize: 18, color: '#6b7280', marginBottom: 24 }}>
              Thank you for your interest in becoming an education agent with CIET. We have received your application and will review it within 3 working days.
            </p>

            <div style={{
              padding: 20,
              background: '#f0fdf4',
              borderRadius: 8,
              border: '1px solid #86efac',
              marginBottom: 24
            }}>
              <h3 style={{ margin: '0 0 8px', color: '#166534' }}>What happens next?</h3>
              <ul style={{ margin: 0, paddingLeft: 20, textAlign: 'left', color: '#166534' }}>
                <li>You will receive an acknowledgment within 24 hours</li>
                <li>We will assess your application within 3 working days</li>
                <li>If approved, we will send you an agreement for signing</li>
                <li>You will participate in an induction program</li>
              </ul>
            </div>

            <Button variant="primary" onClick={() => window.location.reload()}>
              Submit Another Application
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const isLastStep = currentStep === sections.length - 1;

  return (
    <div style={S.page}>
      <motion.div
        style={S.card}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', // aligns logo to top
            gap: 24 
          }}
        >
          
          {/* Logo */}
          <img
            style={{ height: '8rem', width: 'auto' }} // height matches h1
            src={require("../../../images/logo.png")}
            alt="CIET Logo"
          />
          <div >
            <h1 style={S.h1}>Education Agent Application Form</h1>
          <p style={{ ...S.hint, fontSize: 16, marginBottom: 24 }}>
            Complete all sections to apply for partnership with Canberra Institute of Education and Technology (CIET)
          </p>
          </div>
        </motion.div>

        <Stepper
          currentStep={currentStep}
          onStepClick={goToStep}
          completedSteps={completedSteps}
        />

        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderSection()}
        </motion.div>

        <div style={S.navigation}>
          <div>
            {currentStep > 0 && (
              <Button variant="secondary" onClick={prevStep} icon={ChevronLeft}>
                Previous
              </Button>
            )}
          </div>

          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {showErrors && !validation.valid && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 14,
                  color: '#dc2626',
                  padding: '8px 12px',
                  background: '#fef2f2',
                  borderRadius: 8,
                  border: '1px solid #fecaca'
                }}
              >
                <AlertCircle size={16} />
                Please complete required fields
              </motion.div>
            )}
          </div>

          <div>
            {isLastStep ? (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={submitting}
                icon={Send}
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            ) : (
              <Button variant="primary" onClick={nextStep} icon={ChevronRight}>
                Next Section
              </Button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{
                marginTop: 16,
                padding: 16,
                background: '#fef2f2',
                borderRadius: 8,
                border: '1px solid #fecaca',
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}
            >
              <AlertCircle size={20} color="#dc2626" />
              <div>
                <strong style={{ color: '#dc2626' }}>Submission Error</strong>
                <p style={{ margin: '4px 0 0', fontSize: 14, color: '#991b1b' }}>
                  {submitError}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}